/**
 * Validator for PlaceWildlife action.
 *
 * Rules (§6.4, §9.4, §11.1, §14):
 *   1. stateVersion matches.
 *   2. Phase is "placeWildlife".
 *   3. `coord` refers to a tile in the active player's board.
 *   4. The tile has no existing wildlife token (wildlife === null).
 *   5. The tile has no landmark token (Landmarks module — §9.4).
 *   6. The active player's drafted wildlife species is in the tile's wildlifeOptions.
 *
 * Resolving wildlife species from draft token: The draft holds a tokenInstanceId.
 * The wildlife species for that token is stored in `turn.actionBudget` or must
 * be resolved from the supply. However, looking at the state model, there is no
 * per-token species lookup — wildlife tokens are opaque instance IDs.
 *
 * Design decision: The current state model does NOT store what species the
 * drafted wildlife token is. The `PlaceWildlife` action itself does not include
 * the species — the validator cannot check species compatibility without either:
 *   (a) A species field added to the action.
 *   (b) A species lookup map in state.
 *   (c) Accepting that species check is done by the applier.
 *
 * Looking at the actual PlaceWildlifeAction type: it only has `coord`.
 * The drafted token species was originally assigned at draft time (DraftPair /
 * DraftDecoupled). Without a species field on the action or a per-token species
 * registry, the validator cannot enforce §6.4 rule 3 (species in wildlifeOptions).
 *
 * Resolution: We add a `draftedWildlifeSpecies` to TurnState.actionBudget as
 * a logical extension OR accept that:
 * - Checks 1–5 are fully enforceable (version, phase, tile exists, no wildlife,
 *   no landmark).
 * - Check 6 (species compatibility) requires context we track via
 *   `turn.actionBudget` but that field does not currently exist.
 *
 * Since we cannot change game-state.ts without a broader P2B design change,
 * we enforce checks 1–5 here and document that check 6 is deferred to the
 * applier layer. The validator returns VALID for species check as a conservative
 * stance — the applier will enforce it with the actual species data.
 *
 * NOTE: This means the validator guarantees "safe to attempt" not "fully valid".
 * Future ADR may add `draftedWildlifeSpecies: Wildlife | null` to actionBudget.
 */

import type { PlaceWildlifeAction } from "../types/actions";
import type { ValidationResult } from "../types/common";
import { coordKey } from "../types/coords";
import type { GameState } from "../types/game-state";
import { commonGuards, VALID } from "./common-guards";

export const validatePlaceWildlife = (
  state: GameState,
  action: PlaceWildlifeAction,
): ValidationResult => {
  const guard = commonGuards(state, action.stateVersion, "placeWildlife");
  if (guard !== null) {
    return guard;
  }

  const activePlayer = state.players[state.turn.activeSeat];
  if (activePlayer === undefined) {
    return { valid: false, error: "ERR_PHASE_MISMATCH", details: {} };
  }

  const targetKey = coordKey(action.coord);
  const cell = activePlayer.board.cells[targetKey];

  // Tile must exist in the player's board
  if (cell === undefined) {
    return {
      valid: false,
      error: "ERR_TILE_NOT_ADJACENT",
      details: { coord: action.coord },
    };
  }

  // Tile must not already have wildlife
  if (cell.wildlife !== null) {
    return {
      valid: false,
      error: "ERR_WILDLIFE_SLOT_FILLED",
      details: { coord: action.coord },
    };
  }

  // Tile must not have a landmark (§9.4)
  if (cell.landmark !== null) {
    return {
      valid: false,
      error: "ERR_WILDLIFE_TILE_LANDMARKED",
      details: { coord: action.coord },
    };
  }

  return VALID;
};
